import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, fmt, LIVE_PLATFORMS, type LivePlatform } from '../api';

// Was ['meta','pinterest','reddit'] — Google and TikTok had working
// adapters and were simply never offered here.
const CHANNELS = LIVE_PLATFORMS;
const EMOTIONS = ['curiosity', 'desire', 'relief', 'pride', 'fear', 'belonging'] as const;

export default function CampaignLauncher() {
  const nav = useNavigate();
  const [products, setProducts] = useState<any[]>([]);
  const [productId, setProductId] = useState<string>('');
  const [budget, setBudget] = useState(2000);
  // Safe first-launch default: one proven channel. Customers can opt into
  // multi-channel only after deliberately selecting it.
  const [channels, setChannels] = useState<LivePlatform[]>(['meta']);
  const [emotion, setEmotion] = useState<string>('curiosity');
  const [launching, setLaunching] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.products().then((rows) => {
      setProducts(rows);
      if (rows.length) setProductId(rows[0].id);
    });
  }, []);

  function toggleChannel(c: LivePlatform) {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }

  async function saveDraft() {
    setErr(null);
    setLaunching(true);
    try {
      const r = await api.createCampaignDraft({
        productId,
        dailyBudgetCents: budget,
        channels,
        emotion,
      });
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLaunching(false);
    }
  }

  const selectedProduct = products.find((p) => p.id === productId);

  if (result) {
    return (
      <>
        <h2 className="page-title">Campaign draft saved</h2>
        <p className="page-sub">
          Nothing has been published and no advertising spend can occur. Review this draft before launching.
        </p>
        <div className="card card-padded" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--text-sub)' }}>campaign_id</div>
          <code style={{ fontSize: 13 }}>{result.campaignId}</code>
          <div style={{ marginTop: 16 }}><b>Status:</b> paused draft</div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => nav(`/campaigns/${result.campaignId}`)}>
            View campaign →
          </button>
          <button className="btn" onClick={() => setResult(null)}>
            Create another draft
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <h2 className="page-title">Build a campaign draft</h2>
      <p className="page-sub">Choose safe defaults, save a paused draft, then review it before any live launch.</p>

      <div className="card card-padded" style={{ maxWidth: 720 }}>
        <div className="field">
          <label>1. Pick a product</label>
          <select value={productId} onChange={(e) => setProductId(e.target.value)}>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} — {fmt.money(p.price_cents)} · ad-fit {p.ad_fit_score}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>2. Daily budget — {fmt.money(budget)}</label>
          <input
            type="range"
            min={2000}
            max={20000}
            step={500}
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="slider"
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-mute)' }}>
            <span>$20</span>
            <span>$200</span>
          </div>
        </div>

        <div className="field">
          <label>3. Channels</label>
          <div className="checkbox-group">
            {CHANNELS.map((c) => (
              <label key={c} className={channels.includes(c) ? 'on' : ''}>
                <input
                  type="checkbox"
                  checked={channels.includes(c)}
                  onChange={() => toggleChannel(c)}
                  style={{ accentColor: 'var(--brand)' }}
                />
                <span style={{ textTransform: 'capitalize' }}>{c}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="field">
          <label>4. Emotion target (optional)</label>
          <select value={emotion} onChange={(e) => setEmotion(e.target.value)}>
            {EMOTIONS.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>

        <div className="launch-preview">
          This saves a paused draft for <b>{selectedProduct?.title ?? '—'}</b> across{' '}
          <b>{channels.length}</b> selected platform(s). It does not call Meta or spend money.
        </div>

        {err && (
          <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>Error: {err}</div>
        )}

        <button
          className="btn btn-primary"
          onClick={saveDraft}
          disabled={!productId || channels.length === 0 || launching}
        >
          {launching ? 'Saving…' : 'Save paused draft'}
        </button>
      </div>
    </>
  );
}
